import { useEffect, useState } from 'react'
import { Building2 } from 'lucide-react'
import Button from '../../components/Button'
import { FormBody, FormCols } from '../../components/Form'
import FormRow from '../../components/FormRow'
import Modal from '../../components/Modal'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { strings as S } from './strings'
import './modals.css'

/**
 * `#add_modal` — create and edit share one modal, and the legacy header text stays
 * `إضافة إدارة/مجموعة جديدة` in both cases (js.cshtml:575). Kept as-is.
 *
 * The footer carries a cancel beside the save, as every other sheet in the panel
 * does — it had only the save, so the one way out was the × in the corner.
 *
 * @param {{value: number, label: string}[]} types  `dbContext.GroupTypes`, by way of the
 *        page payload. Not a hard-coded pair: the ids come from the database, and a wrong
 *        one here saves a department as a private group without saying so.
 */
export default function GroupFormModal({ open, group, types = [], onClose, onSubmit }) {
  const [values, setValues] = useState({ typeId: '', arabicName: '', englishName: '' })
  const [errors, setErrors] = useState({})

  const options = types.map((type) => ({ value: String(type.value), label: type.label }))

  useEffect(() => {
    if (!open) return
    setErrors({})
    setValues(
      group
        ? {
            typeId: String(group.typeId),
            /* Both names, not the display label. `group.name` is whichever of the two
               matches the current language, so seeding the Arabic field from it would
               overwrite the Arabic name with the English one when the panel is in
               English — and leave the English field empty. */
            arabicName: group.arabicName ?? '',
            englishName: group.englishName ?? '',
          }
        : { typeId: String(types[0]?.value ?? ''), arabicName: '', englishName: '' },
    )
    // `types` only seeds a new group's default; a change to it must not reset an open form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group])

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  // Both name fields use the same rule: notEmpty then /[\s\S]{2,100}/.
  const checkName = (value) => {
    if (!value.trim()) return S.form.required
    if (value.length < 2 || value.length > 100) return S.form.lengthError
    return null
  }

  const submit = (event) => {
    event.preventDefault()
    const next = {}
    if (!values.typeId) next.typeId = S.form.required
    const arabic = checkName(values.arabicName)
    if (arabic) next.arabicName = arabic
    const english = checkName(values.englishName)
    if (english) next.englishName = english

    setErrors(next)
    if (Object.keys(next).length === 0) onSubmit(values, !!group)
  }

  return (
    <Modal
      open={open}
      title={S.form.title}
      icon={Building2}
      hint={S.form.hint}
      onClose={onClose}
      footer={
        <>
          <Button type="submit" form="group-form">
            {S.form.save}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {S.close}
          </Button>
        </>
      }
    >
      <form id="group-form" onSubmit={submit} noValidate>
        <FormBody>
          <FormRow className="form-narrow" label={S.form.type} error={errors.typeId}>
            {(id) => (
              <Select id={id} value={values.typeId} onChange={set('typeId')} options={options} />
            )}
          </FormRow>

          {/* Placeholders dropped: they repeated the label they sat under. */}
          <FormCols>
            <FormRow label={S.form.arabicName} error={errors.arabicName}>
              {(id) => (
                <TextField
                  id={id}
                  value={values.arabicName}
                  onChange={set('arabicName')}
                  invalid={!!errors.arabicName}
                />
              )}
            </FormRow>

            <FormRow label={S.form.englishName} error={errors.englishName}>
              {(id) => (
                <TextField
                  id={id}
                  dir="ltr"
                  value={values.englishName}
                  onChange={set('englishName')}
                  invalid={!!errors.englishName}
                />
              )}
            </FormRow>
          </FormCols>
        </FormBody>
      </form>
    </Modal>
  )
}
